import { useState } from "react";
import { PLACE_TYPE_META, PlaceType } from "~/lib/api";
import { Button } from "~/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "~/components/ui/dialog";
import { Form } from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "~/components/ui/select";
import { Textarea } from "~/components/ui/textarea";

interface PlaceFormProps {
  /** Pre-filled type (undefined = no pre-selection, requires user to pick) */
  type?: PlaceType;
  onSubmit: (data: { name: string; type: PlaceType; description?: string }) => void;
  onCancel: () => void;
  saving?: boolean;
  /** Pre-fill name field. When provided, shows type selector (POI flow). */
  initialName?: string;
  /** Filter type selector to these types. If single type, pre-selects it. If null, shows all types. */
  allowedTypes?: PlaceType[] | null;
}

export default function PlaceForm({
  type: initialType,
  onSubmit,
  onCancel,
  saving = false,
  initialName,
  allowedTypes,
}: PlaceFormProps) {
  const [name, setName] = useState(initialName ?? "");
  const [type, setType] = useState<PlaceType | "">(initialType ?? "");
  const [description, setDescription] = useState("");
  const [error, setError] = useState("");

  // Show type selector when:
  // 1. POI flow (name is pre-filled but type is not)
  // 2. allowedTypes is provided and has multiple types (or is null for all types)
  const showTypeSelector = !!initialName || (allowedTypes !== undefined && allowedTypes?.length !== 1);

  // Get the types to show in the selector
  const typesToShow =
    allowedTypes === null || allowedTypes === undefined
      ? Object.entries(PLACE_TYPE_META)
      : allowedTypes.map((t) => [t, PLACE_TYPE_META[t]] as [PlaceType, { label: string }]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError("Name is required");
      return;
    }
    if (!type) {
      setError("Please select a type");
      return;
    }
    onSubmit({
      name: name.trim(),
      type,
      description: description.trim() || undefined,
    });
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>
            {initialName ?? (initialType ? `Add ${PLACE_TYPE_META[initialType].label}` : "Add Place")}
          </DialogTitle>
          {!initialType && <DialogDescription>This place needs just a few more details.</DialogDescription>}
        </DialogHeader>

        <Form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex flex-col gap-1">
            <Label htmlFor="place-name">Name</Label>
            <Input
              id="place-name"
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setError("");
              }}
              placeholder="Enter place name"
              disabled={saving}
            />
            {error && (
              <p role="alert" className="text-destructive text-xs mt-1">
                {error}
              </p>
            )}
          </div>

          {showTypeSelector && (
            <div className="flex flex-col gap-1">
              <Label htmlFor="place-type">Type</Label>
              <Select value={type} onValueChange={(value) => setType(value as PlaceType)} disabled={saving}>
                <SelectTrigger id="place-type">
                  <SelectValue placeholder="Select a type..." />
                </SelectTrigger>
                <SelectContent>
                  {typesToShow.map(([value, { label }]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* When initial data is provided but type is missing, only type is needed */}
          {!(initialName && !initialType) && (
            <div className="flex flex-col gap-1">
              <Label htmlFor="place-description">
                Description <span className="text-muted-foreground/70">(optional)</span>
              </Label>
              <Textarea
                id="place-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Share a local secret..."
                rows={2}
                className="resize-none"
                disabled={saving}
              />
            </div>
          )}

          <div className="flex gap-2 pt-2 justify-end">
            <Button type="button" variant="ghost" onClick={onCancel} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving || !name.trim() || !type}>
              {saving ? "Adding..." : "Add Place"}
            </Button>
          </div>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
